import { Box, Stack, Typography } from '@mui/material'
import { Chip, Spinner, theme } from '@ketch-com/deck'

interface QueueFlowChartProps {
  rightsReport?: {
    invokedSevenDays?: number
    fulfilledSevenDays?: number
  }
  isLoading?: boolean
}

export const LiveQueueFlowChart = ({ rightsReport, isLoading }: QueueFlowChartProps) => {
  const invokedData = { name: 'Invoked', value: rightsReport?.invokedSevenDays ?? 0 }
  const completeData = { name: 'Complete', value: rightsReport?.fulfilledSevenDays ?? 0 }

  const maxBarHeight = 140
  const minBarHeight = 50
  const maxValue = Math.max(invokedData.value, completeData.value, 1)
  const calcHeight = (value: number) => Math.max((value / maxValue) * maxBarHeight, minBarHeight)

  return (
    <Stack
      sx={{
        background: theme.palette.Common.Container,
        width: '280px',
        height: '266px',
        padding: '16px 20px',
        borderRadius: '16px',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Queue Flow</Typography>
        <Chip label="Last 7 days" size="small" />
      </Stack>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <Spinner size="32px" thickness={2} />
        </Box>
      ) : (
        <Box
          flexBasis={300}
          alignSelf="stretch"
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
          gap={1}
          height="100%"
        >
          {/* Invoked Bar */}
          <Box
            flexBasis={130}
            display="flex"
            flexDirection="column"
            justifyContent="flex-end"
            alignItems="center"
            sx={{ minHeight: maxBarHeight }}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.Violet.Dark.o24,
                width: '100%',
                height: `${calcHeight(invokedData.value)}px`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                mb: 1,
              }}
            >
              <Stack px={1} py={0.75} alignItems="left" width="100%">
                <Typography variant="label" color={theme.palette.Violet.Dark.o100}>
                  {invokedData?.value ?? 0}
                </Typography>
                <Typography variant="label" color={theme.palette.Violet.Dark.o100}>
                  {invokedData?.name ?? 'No Data'}
                </Typography>
              </Stack>
            </Box>
          </Box>
          {/* Complete Bar */}
          <Box
            flexBasis={130}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            flexDirection="column"
            sx={{ minHeight: maxBarHeight }}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.Green.Dark.o24,
                width: '100%',
                height: `${calcHeight(completeData.value)}px`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                mb: 1,
              }}
            >
              <Stack px={1} py={0.75} alignItems="left" width="100%">
                <Typography variant="label" color={theme.palette.Green.Dark.o100}>
                  {completeData?.value ?? 0}
                </Typography>
                <Typography variant="label" color={theme.palette.Green.Dark.o100}>
                  {completeData?.name ?? 'No Data'}
                </Typography>
              </Stack>
            </Box>
          </Box>
        </Box>
      )}
    </Stack>
  )
}
